/**
 * 🔴 #339 (20/09/2026) — A LINHA QUE RECOLHE NO CELULAR. **Um componente só**, servindo o **Levantamento** e o **Compras**.
 *
 * ⚠️ O problema medido em 18/09: a aba Levantamento tem **24.442 px de altura em 375 px** — 90 linhas viraram 90 cartões. A régua
 * de mobile estava funcionando (tabela larga vira cartão), mas quem está em obra rola um prédio para achar um item.
 *
 * ✅ O desenho: a linha aparece **recolhida**, com o essencial — **nome · quantidade · estado** —, e **toca e expande**. O alvo de
 * toque tem **44 px** (é o #340, que cai junto). ❌ Zero `localStorage`: o padrão é recolhido e não persistir é aceitável.
 *
 * 🔴 **Dois jeitos de recolher, um componente** (`recolhe`):
 *  - `'sempre'` — a **cotação do Compras**: a decisão LITERAL do Matheus (17/09, BUGS #339) é *"o ITEM NASCE RECOLHIDO"*, com a
 *    linha **descrição · qtd · fechado · margem · estado**, em qualquer largura (a meta dele era ~4.400 px em 1920 para 55 itens).
 *    O item que ele abriu **fica aberto** até ele fechar.
 *  - `'no-celular'` — o **levantamento**: em tela larga ele é uma TABELA (uma linha por item, já curta); o cartão só existe no
 *    celular, e é ele que recolhe. Aqui o cabeçalho some em tela larga, e o miolo aparece sempre.
 * ⚠️ A primeira versão desta rodada deixava a cotação aberta no desktop "para não mudar o dia do Gabriel" — era decisão minha
 * passando por cima de uma dele. O BUGS #339 guardava a dele; corrigido antes do commit.
 */
import { useState, type ReactNode } from 'react';

import { cn } from '@/lib/cn';
import { useTelaDeCelular } from '@/lib/use-tela-de-celular';

import { CLASSES_DO_TOM, type TomDoAviso } from './tom-do-aviso';

export interface LinhaRecolhivelProps {
  /** O nome do item — o que a pessoa procura. */
  titulo: ReactNode;
  /** A quantidade, do jeito que cada tela escreve ("5 un", "10 de 12"). */
  quantidade?: ReactNode;
  /** O estado: a etiqueta que diz onde a linha está (em cotação, comprado, sem código…). */
  estado?: ReactNode;
  /** O resto — só aparece quando a linha está aberta. */
  children: ReactNode;
  /** Começa aberta mesmo no celular (ex.: a linha que acabou de ser tocada por um aviso). */
  comecaAberta?: boolean;
  /** `'sempre'` recolhe em qualquer largura (a cotação); `'no-celular'` só no celular (o levantamento). Padrão: `'no-celular'`. */
  recolhe?: 'sempre' | 'no-celular';
  /**
   * 🆕 CRONOGRAMA-MSP (09/10/2026) — porta NOVA e opcional para o AVISO (a regra "aviso é uma linha; o detalhe só ao
   * clicar"): pinta a borda e o fundo no tom do aviso. Sem `tom`, nada muda para quem já usava (Levantamento, Compras).
   * Quem consome é o `<Aviso>` — ❌ não se monta aviso com `Alert` aberto.
   */
  tom?: TomDoAviso;
  className?: string;
  'data-testid'?: string;
}


/**
 * A linha recolhível. No celular nasce fechada (nome · quantidade · estado) e abre no toque; em tela larga o miolo aparece sempre,
 * sem cabeçalho — exatamente como era antes.
 */
export function LinhaRecolhivel({
  titulo,
  quantidade,
  estado,
  children,
  comecaAberta,
  recolhe = 'no-celular',
  tom,
  className,
  'data-testid': testId,
}: LinhaRecolhivelProps) {
  const celular = useTelaDeCelular();
  const [aberta, setAberta] = useState<boolean | null>(null);
  const sempre = recolhe === 'sempre';
  // Enquanto ninguém tocou: 'sempre' nasce fechada; 'no-celular' nasce fechada só no celular.
  const estaAberta = aberta ?? (!!comecaAberta || (!sempre && !celular));

  return (
    <div
      className={cn('rounded-lg border', tom ? CLASSES_DO_TOM[tom] : 'border-line dark:border-line-dark', className)}
      data-testid={testId}
      data-aberta={estaAberta ? 'sim' : 'nao'}
    >
      <button
        type="button"
        onClick={() => setAberta(!estaAberta)}
        aria-expanded={estaAberta}
        // no modo 'no-celular' o cabeçalho só existe no celular (em tela larga o levantamento é tabela e o miolo aparece sempre)
        className={cn('flex w-full min-h-[44px] items-start gap-2 px-3 py-2 text-left', !sempre && 'sm:hidden')}
        data-testid={testId ? `${testId}-cabecalho` : undefined}
      >
        {/* ⚠️ Duas linhas, de propósito: o nome EM CIMA, com a largura inteira; a quantidade e o estado EMBAIXO, quebrando.
            Na primeira versão os três dividiam a mesma linha, a quantidade não quebrava, e em 375 px o nome ficava com ~0 px de
            largura (uma letra por linha, cabeçalho de 830 px): a aba ficou MAIOR que antes. Foi a medição que pegou. */}
        <span className="min-w-0 flex-1">
          <span className="block break-words text-sm text-ink-1 dark:text-ink-dark-1">{titulo}</span>
          {(quantidade != null || estado != null) && (
            <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-2 dark:text-ink-dark-2">
              {quantidade != null && <span className="tabular-nums">{quantidade}</span>}
              {estado != null && <span className="min-w-0">{estado}</span>}
            </span>
          )}
        </span>
        <span
          aria-hidden
          className={cn(
            'mt-0.5 shrink-0 text-ink-3 transition-transform dark:text-ink-dark-3',
            estaAberta ? 'rotate-90' : 'rotate-0',
          )}
        >
          ›
        </span>
      </button>
      {/* em tela larga o miolo aparece sempre (sem cabeçalho para recolher); no celular, só aberto */}
      {sempre ? (
        estaAberta && <div className="border-t border-line px-3 py-2 dark:border-line-dark">{children}</div>
      ) : (
        <div
          className={cn(
            'px-3 py-2 sm:border-t-0',
            estaAberta ? 'block border-t border-line dark:border-line-dark' : 'hidden sm:block',
          )}
        >
          {estaAberta || !celular ? children : null}
        </div>
      )}
    </div>
  );
}
